<template>
<div class="dark:bg-gray-800">
  <!-- Hero Section -->
  <section class="sticky dark:bg-gray-800">
    <div class="max-w-4xl px-4 sm:pt-24 pt-12 sm:pb-4 mx-auto text-center">
      
      <!-- Badge -->
      <span class="inline-flex items-center px-4 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 mb-6">
        <font-awesome-icon icon="fa-solid fa-rocket" class="mr-2" /> {{ t('home.badge_new') }}
      </span>
      
      <h1 class="font-extrabold leading-tight tracking-tight text-gray-800 text-4xl sm:text-6xl lg:text-8xl dark:text-white">
        <span class="block">{{ t('home.display_line1') }}</span>
        <span class="block mt-2 bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
          {{ t('home.display_line2') }}
        </span>
      </h1>

      <!-- Value Proposition -->
      <p class="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto mt-6">
        {{ t('home.value_proposition') }}
        <strong class="text-blue-600 dark:text-blue-400">{{ t('home.value_highlight') }}</strong>
        {{ t('home.value_end') }}
      </p>
    </div>

    <!-- CTAs -->
    <div class="max-w-lg px-4 pb-8 mx-auto text-center">
      <div class="flex flex-col sm:flex-row justify-center items-center gap-4 py-4">
        <button 
          @click="registerClicked" 
          class="w-full sm:w-auto backdrop-blur-sm transition duration-300 ease-in-out bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white text-lg font-semibold py-3 px-8 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
        >
          <span>{{ t('home.button_1') }}</span>
        </button>

        <router-link 
          to="/about"
          class="w-full sm:w-auto backdrop-blur-sm transition duration-300 ease-in-out bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 text-gray-800 dark:text-white hover:border-blue-500 dark:hover:border-blue-400 text-lg font-semibold py-3 px-8 rounded-full"
        >
          <span>{{ t('home.button_2') }}</span>
        </router-link>
      </div>
    </div>

    <!-- Social Proof -->
    <div class="max-w-lg mx-auto px-4 pb-12">
      <div class="flex flex-col sm:flex-row items-center justify-center gap-4">
        <!-- User Avatars -->
        <div class="flex -space-x-3">
          <div class="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-white dark:border-gray-800 flex items-center justify-center text-white text-sm font-bold">J</div>
          <div class="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-green-600 border-2 border-white dark:border-gray-800 flex items-center justify-center text-white text-sm font-bold">M</div>
          <div class="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 border-2 border-white dark:border-gray-800 flex items-center justify-center text-white text-sm font-bold">A</div>
          <div class="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 border-2 border-white dark:border-gray-800 flex items-center justify-center text-white text-sm font-bold">+</div>
        </div>
        <div class="text-center sm:text-left">
          <div class="flex items-center justify-center sm:justify-start gap-1 text-yellow-500">
            <font-awesome-icon v-for="n in 5" :key="n" icon="fa-solid fa-star" />
          </div>
          <p class="text-sm text-gray-600 dark:text-gray-400">
            {{ t('home.social_proof') }}
          </p>
        </div>
      </div>
    </div>
  </section>

  <!-- Features Section -->
  <div class="text-left dark:bg-gray-800 pb-16">
    <div class="sm:px-8 lg:px-28">
      <section class="relative flex items-center w-full">
        <div class="relative items-center w-full px-5 mx-auto md:px-12 lg:px-16 max-w-7xl">
          <div class="relative flex-col items-start m-auto align-middle">
            <div class="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-16">
              <div class="relative items-center gap-12 m-auto lg:inline-flex md:order-first">
                <div class="max-w-xl text-center lg:text-left">
                  <div>
                    <p class="text-3xl font-bold tracking-tight text-gray-800 sm:text-4xl dark:text-white">
                      {{ t('home.title') }}
                    </p>
                    <p class="max-w-xl mt-4 text-base leading-relaxed tracking-tight text-gray-600 dark:text-gray-300">
                      {{ t('home.message') }}
                    </p>
                  </div>
                  
                  <!-- Quick Benefits -->
                  <div class="mt-8 space-y-3">
                    <div class="flex items-center gap-3">
                      <div class="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center">
                        <font-awesome-icon icon="fa-solid fa-check" class="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <span class="text-gray-700 dark:text-gray-300">{{ t('home.benefit_1') }}</span>
                    </div>
                    <div class="flex items-center gap-3">
                      <div class="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                        <font-awesome-icon icon="fa-solid fa-bolt" class="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <span class="text-gray-700 dark:text-gray-300">{{ t('home.benefit_2') }}</span>
                    </div>
                    <div class="flex items-center gap-3">
                      <div class="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                        <font-awesome-icon icon="fa-solid fa-shield-halved" class="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <span class="text-gray-700 dark:text-gray-300">{{ t('home.benefit_3') }}</span>
                    </div>
                  </div>

                  <div class="flex justify-center gap-3 mt-10 lg:justify-start">
                    <router-link 
                      to="/about"
                      class="inline-flex items-center justify-center text-sm font-semibold text-orange-600 dark:text-orange-400 duration-200 hover:text-orange-700 dark:hover:text-orange-300"
                    >
                      {{ t('home.readmore') }} &nbsp; →
                    </router-link>
                  </div>
                </div>
              </div>
              <div class="order-first block w-full mt-12 lg:mt-0">
                <img 
                  class="object-cover rounded-3xl object-center w-full mx-auto shadow-2xl lg:ml-auto" 
                  alt="Smart Replace Demo" 
                  src="../assets/images/5.png" 
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</div>
</template>

<script setup>
import { ref, inject } from "vue";
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const emitter = inject('emitter');

const registerClicked = () => {
	emitter.emit('registerClicked', 'SignUp');
};

</script>

